import type {
  AttributionFeeCorrection,
  AttributionFeeStanding,
  AttributionServiceStage,
  AttributionServiceStep,
} from "@ondo/shared";

/**
 * The words beside OND-60's service case.
 *
 * EVERY RECORD HERE IS TOTAL OVER ITS UNION, which is the device
 * `verifications.constants.ts` uses for `OUTCOME_LABELS` and
 * `notifications.constants.ts` for `TAB_OF`, and it is here for a reason this
 * ticket learnt the hard way: a stage or a fee standing added in SQL and
 * forgotten here would render as a blank beside a picture, or — worse — as
 * nothing at all beside a figure somebody owes. A compile error is the only
 * reminder that cannot be ignored.
 *
 * AND THE COPY SAYS WHAT ONDO DOES AND DOES NOT DO. The two sentences this file
 * is most careful about are the target, which is not a guarantee of
 * authentication, and the fee, which is recorded and never collected. Both are
 * the owner's own words and both are easy to lose in a shorter label.
 */

/**
 * The $250 and the thirty days, as the screens say them.
 *
 * READ FROM `@ondo/shared` RATHER THAN WRITTEN HERE, because
 * `ATTRIBUTION_FEE_MINOR` is mirrored against `ondo.attribution_tariff()` by an
 * assertion in `rls.sh` and a third copy would be outside it. `SELL_COPY.fee`
 * made the same mistake once and now reads from `REGISTRATION_FEE_MINOR`.
 */
export const SERVICE_COPY = {
  heading: "Studio confirmation service",
  /**
   * SAID BEFORE ANYTHING ELSE ON THIS SECTION, because it is the sentence a
   * reader is most likely to supply wrongly for themselves. OND-60: "Show the
   * target clearly; it is not guaranteed authentication."
   */
  lead:
    "A studio's confirmation is a paid service with a target date. The target is what the studio aims at, not a promise of an answer — and no answer is ever assumed from silence.",
  /**
   * The money sentence, in the register `20260910110000` decision 1 set and
   * the Transaction Centre keeps: recorded, never taken.
   */
  feeNote:
    "ONDO records what is owed and what somebody says was paid. It holds no money, takes none and returns none.",
  overdueBadge: "Overdue",
  awaitingBadge: "Awaiting the studio",
  targetLabel: "Target",
  revisedLabel: "Revised target",
  originalLabel: "Originally",
  reviewerLabel: "With",
  escalatedLabel: "escalated",
  noReviewer: "Not yet assigned",
  logHeading: "What has happened",
  feeHeading: "The fee",
  reviewersHeading: "Who has held it",
  /** Shown where a case has no fee, which is every case nobody has answered. */
  noFee: "Nothing is owed: no substantive answer has been given.",
  /** The one thing an assignment must never be read as. */
  assignmentNote:
    "Assigning a case does not let the reviewer answer it. Answering still needs the standing the studio already gave them, and signing still needs live authority for the artist.",
  evidenceNote:
    "Asking for more evidence keeps the request open and charges nothing. It is not an answer and not a refusal.",
  extensionNote:
    "A revised target is recorded beside the original with its reason. Neither date does anything when it passes.",
  empty: {
    lead: "No studio confirmation cases.",
    detail:
      "A case appears here when somebody asks an authorised studio to confirm that a work is by its artist — on either side of the question.",
  },
} as const;

/**
 * What each stage is called.
 *
 * `Record<AttributionServiceStage, …>` — all eight, and the four that are not
 * OND-60's named outcomes are the ones this map exists for. A screen that
 * collapsed `no_recall`, `referred` and `cancelled` into "declined" would tell a
 * collector the studio refused when it did not, which is the mistake
 * `20260808085900` refused to make in SQL.
 */
export const STAGE_LABELS: Record<AttributionServiceStage, string> = {
  awaiting: "Awaiting the studio",
  evidence_requested: "More evidence wanted",
  confirmed: "Confirmed by the studio",
  rejected: "Rejected by the studio",
  declined: "The studio declined to give an opinion",
  no_recall: "The studio does not recall it",
  referred: "Passed to another party",
  cancelled: "Withdrawn before an answer",
};

/** The sentence under the label. What it means, and what it does not. */
export const STAGE_DETAILS: Record<AttributionServiceStage, string> = {
  awaiting:
    "The studio has the claim and its evidence. Silence is neither acceptance nor rejection, and nothing here expires.",
  evidence_requested:
    "The studio has asked for more before it answers. The request is still open and nothing has been charged.",
  confirmed: "The studio has confirmed the attribution. Its answer stands on the work's history.",
  // NOT "disputed", and not softened either. A rejection is an answer the
  // studio stands behind, and it is charged exactly as a confirmation is.
  rejected:
    "The studio has rejected the attribution. That is a substantive answer and is charged as one; disagreeing with it is not a reason for a refund.",
  declined:
    "The studio has declined to give an opinion. Nothing has been charged, and the claim stands unconfirmed rather than rejected.",
  // The sentence `20260808085900` wrote the outcome for: "a registry that
  // forced it into 'declined' would be recording a shrug as a rebuff."
  no_recall:
    "Somebody looked and does not remember. That is not a refusal and not a rejection, and nothing has been charged.",
  referred:
    "The party asked passed the question to somebody better placed. A case of their own is open, with a target of its own.",
  cancelled: "The request was withdrawn before an answer. Nothing was charged.",
};

/** What each line of the service log is called. */
export const STEP_LABELS: Record<AttributionServiceStep, string> = {
  target: "Target set",
  evidence_requested: "More evidence wanted",
  extension: "Target revised",
};

/**
 * What a fee is standing at.
 *
 * "Recorded as paid" AND NEVER "Paid", which is the Transaction Centre's rule
 * and `20260910110000` decision 1's: ONDO observed nothing, and the studio's
 * assertion is what the row holds.
 */
export const FEE_STANDING_LABELS: Record<AttributionFeeStanding, string> = {
  due: "Recorded as owed",
  recorded_paid: "Recorded as paid",
  void: "Voided",
  refund_due: "Refund owed",
  refunded: "Refund recorded",
};

export const FEE_STANDING_DETAILS: Record<AttributionFeeStanding, string> = {
  due: "The studio has recorded that this is owed. ONDO is not collecting it.",
  recorded_paid: "The studio has recorded that the money arrived. ONDO did not observe it.",
  void: "Raised in error, and owed by nobody. The original line is kept.",
  refund_due: "The studio has recorded that it owes this back. ONDO is not returning it.",
  refunded: "The studio has recorded returning this, elsewhere. The original fee and payment are kept.",
};

/** What each correction on a fee is called. */
export const CORRECTION_LABELS: Record<AttributionFeeCorrection, string> = {
  void: "Voided",
  refund_due: "Refund owed",
  refund_recorded: "Refund recorded",
};

/** The three things the studio may do to a live case, as the controls say them. */
export const SERVICE_ACTIONS = {
  evidence: "Ask for more evidence",
  extension: "Revise the target",
  reviewer: "Assign a reviewer",
  escalate: "Escalate",
  recordPaid: "Record as paid",
  correct: "Correct this fee",
} as const;

/** The labels on each form field, and what each one must say. */
export const SERVICE_FIELDS = {
  evidenceReason: "What do you need?",
  evidencePlaceholder: "Two raking-light photographs of the signature, and the studio invoice.",
  extensionDate: "Revised target date",
  extensionReason: "Why?",
  extensionPlaceholder: "The archive is closed for rehousing until the end of November.",
  reviewer: "Reviewer's ONDO id",
  reviewerReason: "Why? (required to escalate)",
  paidNote: "How it was paid",
  paidPlaceholder: "Bank transfer, ref WURM-0251.",
  correctionKind: "What sort of correction",
  correctionReason: "Why?",
  correctionReference: "External reference, if there is one",
} as const;
